// Drive history — the trips this person drove, newest first.
//
// Was a collapsed accordion of `ListItem`s, each led by a circled driver-type icon and
// a timestamp, with the route on a second line and the status chip absolutely positioned
// at `left: 200px`. Now hairline `Row`s in the record idiom: the route leads, because it
// is what a support agent is scanning for, and the link goes where the old one did — the
// daily trips page, full screen.
//
// The driver-type icon is gone rather than restyled. It was the same icon on every row of
// one person's history, so it distinguished nothing; the fact it carried (on-call vs going
// anyway) is a field on Identity, where it is stated once.
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters } from '../../../hooks';
import { getDriveHistory } from '../../../redux/Users/actions';
import { Box, Pagination, Typography } from '../../../ui/core';
import { hairline } from '../../../ui/styles';
import { DriveIcon } from '../../../ui/icons';
import TripStatusChip from '../Trips/TripStatusChip';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  PageLink,
  TrailingMeta,
  Meta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

const DriveItem = ({ trip, first }) => {
  const f = useFormatters();

  const route =
    trip.originCity && trip.destinationCity
      ? `${trip.originCity.cityName} → ${trip.destinationCity.cityName}`
      : 'Route unavailable';

  const departureDate = f.date.toLocaleString(trip.departureTime, f.date.DateFormats.DATE_ISO);
  const completed = trip.status === 'completed';

  return (
    <Row
      first={first}
      // Date and time stacked in the gutter: they are one fact, and the time was on the
      // row's meta line where it sat next to the route for no reason.
      at={f.date.toLocaleString(trip.departureTime, f.date.DateFormats.DATE_SHORT)}
      atMeta={f.date.toLocaleString(trip.departureTime, f.date.DateFormats.TIME_SIMPLE)}
      // Both halves of the link target are required — a trip with neither is not
      // openable, so it stays plain text rather than becoming a link to nowhere.
      label={
        departureDate && trip._id ? (
          <PageLink to={`/trips/daily?date=${departureDate}&tripId=${trip._id}&fullScreen=true`}>{route}</PageLink>
        ) : (
          route
        )
      }
      // The payout moves onto the meta line so the status chip is the ONLY thing on the
      // trailing edge. Chips are variable-width ("Completed" vs "Finalizing details"), so
      // anything to their right was pushed to a different x on every row — the amounts
      // formed a ragged column and the chips never lined up at all.
      //
      // Green only when the money actually moved. A cancelled trip still carries a payout
      // figure, and printing it in the same colour reads as paid.
      meta={
        <Typography
          component="span"
          sx={{ fontSize: '11.5px', fontWeight: 600, color: completed ? 'success.main' : 'text.secondary' }}
        >
          {completed ? '+' : ''}
          {f.number.currency(trip.payout)}
        </Typography>
      }
      right={<TripStatusChip status={trip.status} />}
    />
  );
};

const DriveHistoryCard = ({ user }) => {
  const dispatch = useDispatch();
  const userDriveHistory = useSelector((state) => state.users.selection.driveHistory);
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const pages = userDriveHistory ? Math.ceil(userDriveHistory.count / pageSize) : 0;

  const onPageChange = (_, value) => {
    setPage(value);
  };

  const load = useCallback(
    () => getDriveHistory(user.id, (page - 1) * pageSize, pageSize)(dispatch),
    [page, dispatch, user.id],
  );
  const [status, retry] = useSectionFetch(load);

  return (
    <SectionCard
      title="Drive history"
      icon={<DriveIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        // `count` and not just truthiness: the reducer falls back to `[]` on a missing
        // payload, and that array has no `count` — "undefined total" is worse than none.
        Number.isFinite(userDriveHistory?.count) ? (
          <TrailingMeta>
            <Meta>{userDriveHistory.count} total</Meta>
          </TrailingMeta>
        ) : null
      }
    >
      {status === 'loading' && <SectionSkeleton />}
      {status === 'failed' && (
        <SectionError onRetry={retry}>Couldn&rsquo;t load this driver&rsquo;s drives.</SectionError>
      )}
      {/* `data` is missing entirely when the reducer's `|| []` fallback ran, so both
          shapes have to count as empty rather than one of them falling through to a
          `.map` on undefined. */}
      {status === 'done' && !userDriveHistory?.data?.length && <EmptyRows>No drives yet.</EmptyRows>}
      {status === 'done' &&
        userDriveHistory?.data?.map((trip, idx) => (
          <DriveItem key={`${trip._id}-${idx}`} first={idx === 0} trip={trip} />
        ))}
      {pages > 1 && (
        <Box sx={{ px: 1.75, py: 1.25, borderTop: (t) => `1px solid ${hairline(t)}` }}>
          {/* `page` was never passed here either, so the control drifted from the page
              actually fetched. Same fix as Ride history. */}
          <Pagination count={pages} page={page} size="small" onChange={onPageChange} />
        </Box>
      )}
    </SectionCard>
  );
};

export default DriveHistoryCard;
